import * as React from 'react'
import { Plus, Trash2 } from 'lucide-react'
import type { SalesPriceListDto } from '@lib/dto/sales-price-list.dto'
import type { SalesItemKind } from '@lib/enums/sales-item-kind.enum'
import { putSalesPriceListInputSchema, type PutSalesPriceListInput } from '@lib/input/sales-price-list.input'
import { milliToPercent, percentToMilli } from '@lib/helpers/sales-money'
import { WEEKDAY_NAMES } from '@lib/helpers/sales-prices'
import type { CalendarDay } from '@lib/validators/temporal.validator'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { DatePicker } from '@/components/ui/date-picker'
import { Field } from '@/components/ui/field'
import { FormActions } from '@/components/ui/form-actions'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/components/ui/toast'
import { FormError } from '@/components/ui/form-error'
import { Card } from '@/components/ui/card'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useSaveSalesPriceList } from '@/hooks/useSales'
import { centsToInput, inputToCents } from '@/lib/price'
import { cn } from '@/lib/utils'

interface SeasonDraft {
  key: string
  name: string
  startsOn: CalendarDay | ''
  endsOn: CalendarDay | ''
}

interface PriceRowDraft {
  key: string
  seasonKey: string | null
  weekdays: number[]
  price: string
}

interface ItemDraft {
  key: string
  id: string | undefined
  kind: SalesItemKind
  trackId: string | null
  name: string
  description: string
  price: string
  taxable: boolean
  serviceCharged: boolean
  prices: PriceRowDraft[]
}

let counter = 0
/** A key for a row made here, before it has an id. */
function newKey(): string {
  counter += 1
  return `new-${String(counter)}`
}

const WEEKDAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const

function draftsOf(list: SalesPriceListDto): { seasons: SeasonDraft[]; items: ItemDraft[] } {
  return {
    seasons: list.seasons.map((s) => ({ key: s.id, name: s.name, startsOn: s.startsOn, endsOn: s.endsOn })),
    items: list.items.map((item) => ({
      key: item.id,
      id: item.id,
      kind: item.kind,
      trackId: item.trackId,
      name: item.name,
      description: item.description,
      price: centsToInput(item.priceCents),
      taxable: item.taxable,
      serviceCharged: item.serviceCharged,
      prices: item.prices.map((p) => ({ key: newKey(), seasonKey: p.seasonId, weekdays: p.weekdays, price: centsToInput(p.priceCents) })),
    })),
  }
}

function blankItem(kind: SalesItemKind): ItemDraft {
  return {
    key: newKey(),
    id: undefined,
    kind,
    trackId: null,
    name: '',
    description: '',
    price: '',
    taxable: true,
    serviceCharged: kind !== 'add_on',
    prices: kind === 'package' ? [{ key: newKey(), seasonKey: null, weekdays: [5, 6, 0], price: '' }] : [],
  }
}

/** The form as the API takes it, or why it can't be saved yet. */
function toInput(
  rates: { tax: string; service: string; serviceTaxed: boolean },
  seasons: readonly SeasonDraft[],
  items: readonly ItemDraft[],
): PutSalesPriceListInput | string {
  const taxMilli = percentToMilli(rates.tax === '' ? '0' : rates.tax)
  const serviceChargeMilli = percentToMilli(rates.service === '' ? '0' : rates.service)
  if (taxMilli === null) return 'Enter the sales tax as a percentage, like 8.875.'
  if (serviceChargeMilli === null) return 'Enter the service charge as a percentage, like 20.'
  const seasonInputs: PutSalesPriceListInput['seasons'] = []
  for (const s of seasons) {
    if (s.startsOn === '' || s.endsOn === '') return `Give ${s.name === '' ? 'every season' : s.name} its dates.`
    seasonInputs.push({ key: s.key, name: s.name, startsOn: s.startsOn, endsOn: s.endsOn })
  }
  const itemInputs: PutSalesPriceListInput['items'] = []
  for (const item of items) {
    const label = item.name === '' ? 'every item' : item.name
    const prices: PutSalesPriceListInput['items'][number]['prices'] = []
    for (const row of item.prices) {
      const cents = inputToCents(row.price)
      if (cents === null) return `Enter each price for ${label} in dollars.`
      prices.push({ seasonKey: row.seasonKey, weekdays: row.weekdays, priceCents: cents })
    }
    const priceCents = item.kind === 'package' ? null : inputToCents(item.price)
    if (item.kind !== 'package' && priceCents === null) return `Enter a price for ${label} in dollars.`
    itemInputs.push({
      ...(item.id === undefined ? {} : { id: item.id }),
      kind: item.kind,
      trackId: item.trackId,
      name: item.name,
      description: item.description,
      priceCents,
      taxable: item.taxable,
      serviceCharged: item.serviceCharged,
      prices: item.kind === 'package' ? prices : [],
    })
  }
  const parsed = putSalesPriceListInputSchema.safeParse({
    rates: { taxMilli, serviceChargeMilli, serviceChargeTaxed: rates.serviceTaxed },
    seasons: seasonInputs,
    items: itemInputs,
  })
  if (parsed.success) return parsed.data
  const issue = parsed.error.issues[0]
  if (issue === undefined) return 'Something here isn’t complete.'
  if (issue.code === 'custom') return issue.message
  if (issue.path.map(String).includes('name')) return 'Give every season, package and item a name.'
  return issue.message
}

/** Days of the week as small toggles: S M T W T F S. */
function WeekdayPicker({ value, onChange, label }: { value: readonly number[]; onChange: (days: number[]) => void; label: string }) {
  return (
    <div role="group" aria-label={label} className="flex gap-1">
      {WEEKDAY_LETTERS.map((letter, day) => {
        const on = value.includes(day)
        return (
          <button
            key={WEEKDAY_NAMES[day]}
            type="button"
            aria-pressed={on}
            aria-label={WEEKDAY_NAMES[day]}
            onClick={() => onChange(on ? value.filter((d) => d !== day) : [...value, day].sort())}
            className={cn(
              'h-7 w-7 rounded-md border text-xs font-medium',
              on ? 'border-primary bg-primary text-on-primary' : 'border-line bg-surface text-ink-muted hover:text-ink',
            )}
          >
            {letter}
          </button>
        )
      })}
    </div>
  )
}

function TrackSelect({
  value,
  tracks,
  onChange,
  label,
}: {
  value: string | null
  tracks: SalesPriceListDto['tracks']
  onChange: (trackId: string | null) => void
  label: string
}) {
  if (tracks.length < 2) return null
  return (
    <Select size="sm" aria-label={label} value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}>
      <option value="">Every track</option>
      {tracks.map((t) => (
        <option key={t.id} value={t.id}>
          {t.name} only
        </option>
      ))}
    </Select>
  )
}

function Charges({ item, onChange }: { item: ItemDraft; onChange: (patch: Partial<ItemDraft>) => void }) {
  return (
    <div className="flex flex-wrap gap-4 text-sm text-ink">
      <label className="flex items-center gap-2">
        <Checkbox checked={item.taxable} onCheckedChange={(taxable) => onChange({ taxable })} />
        Taxed
      </label>
      <label className="flex items-center gap-2">
        <Checkbox checked={item.serviceCharged} onCheckedChange={(serviceCharged) => onChange({ serviceCharged })} />
        Service charge
      </label>
    </div>
  )
}

/** A package: its name, where it's offered, and its prices by season and day of the week. */
function PackageCard({
  item,
  seasons,
  tracks,
  onChange,
  onRemove,
}: {
  item: ItemDraft
  seasons: readonly SeasonDraft[]
  tracks: SalesPriceListDto['tracks']
  onChange: (patch: Partial<ItemDraft>) => void
  onRemove: () => void
}) {
  const updateRow = (key: string, patch: Partial<PriceRowDraft>): void =>
    onChange({ prices: item.prices.map((row) => (row.key === key ? { ...row, ...patch } : row)) })
  const name = item.name === '' ? 'this package' : item.name
  return (
    <Card asChild className="space-y-3 p-4">
      <li>
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Package" className="min-w-[14rem] flex-1">
            <Input value={item.name} onChange={(e) => onChange({ name: e.target.value })} placeholder="Classic wedding" />
          </Field>
          <TrackSelect value={item.trackId} tracks={tracks} onChange={(trackId) => onChange({ trackId })} label={`Where ${name} is offered`} />
          <Button variant="ghost" size="sm" aria-label={`Remove ${name}`} onClick={onRemove}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
        <Textarea
          aria-label={`What ${name} includes`}
          value={item.description}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder="What it includes"
          rows={2}
        />
        <Charges item={item} onChange={onChange} />
        <div className="space-y-2">
          <p className="m-0 text-xs font-medium text-ink-muted">Prices</p>
          {item.prices.map((row) => (
            <div key={row.key} className="flex flex-wrap items-center gap-3">
              <Select
                size="sm"
                aria-label="Season"
                value={row.seasonKey ?? ''}
                onChange={(e) => updateRow(row.key, { seasonKey: e.target.value === '' ? null : e.target.value })}
              >
                <option value="">Any other date</option>
                {seasons.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.name === '' ? 'Unnamed season' : s.name}
                  </option>
                ))}
              </Select>
              <WeekdayPicker value={row.weekdays} onChange={(weekdays) => updateRow(row.key, { weekdays })} label="Days of the week" />
              <div className="flex items-center gap-1">
                <span className="text-sm text-ink-muted">$</span>
                <Input
                  size="sm"
                  inputMode="decimal"
                  aria-label="Price"
                  className="w-28 text-right tabular-nums"
                  value={row.price}
                  onChange={(e) => updateRow(row.key, { price: e.target.value })}
                />
              </div>
              <Button
                variant="ghost"
                size="sm"
                aria-label="Remove this price"
                onClick={() => onChange({ prices: item.prices.filter((r) => r.key !== row.key) })}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() => onChange({ prices: [...item.prices, { key: newKey(), seasonKey: null, weekdays: [], price: '' }] })}
          >
            <Plus className="h-3.5 w-3.5" />
            Add a price
          </Button>
        </div>
      </li>
    </Card>
  )
}

/** A per-guest price or an add-on: one price, on one line. */
function ItemRow({
  item,
  tracks,
  perGuest,
  onChange,
  onRemove,
}: {
  item: ItemDraft
  tracks: SalesPriceListDto['tracks']
  perGuest: boolean
  onChange: (patch: Partial<ItemDraft>) => void
  onRemove: () => void
}) {
  const name = item.name === '' ? 'this item' : item.name
  return (
    <li className="flex flex-wrap items-center gap-3 border-b border-line py-2.5 last:border-b-0">
      <Input
        size="sm"
        aria-label="Name"
        className="min-w-[12rem] flex-1"
        value={item.name}
        onChange={(e) => onChange({ name: e.target.value })}
        placeholder={perGuest ? 'Plated dinner' : 'Extra hour'}
      />
      <div className="flex items-center gap-1">
        <span className="text-sm text-ink-muted">$</span>
        <Input
          size="sm"
          inputMode="decimal"
          aria-label={`Price of ${name}`}
          className="w-28 text-right tabular-nums"
          value={item.price}
          onChange={(e) => onChange({ price: e.target.value })}
        />
        {perGuest ? <span className="text-sm text-ink-muted">per guest</span> : null}
      </div>
      <TrackSelect value={item.trackId} tracks={tracks} onChange={(trackId) => onChange({ trackId })} label={`Where ${name} is offered`} />
      <Charges item={item} onChange={onChange} />
      <Button variant="ghost" size="sm" aria-label={`Remove ${name}`} onClick={onRemove}>
        <Trash2 className="h-4 w-4" />
      </Button>
    </li>
  )
}

function SectionTitle({ title, hint }: { title: string; hint: string }) {
  return (
    <div>
      <h3 className="m-0 text-sm font-semibold text-ink">{title}</h3>
      <p className="m-0 text-sm text-ink-muted">{hint}</p>
    </div>
  )
}

/**
 * A venue's price list (docs/sales.spec.md, "The price list"): tax and service
 * charge, seasons, packages priced by season and day of the week, per-guest
 * prices and add-ons. Saved whole. A packet already sent keeps its prices.
 */
export function PriceListEditor({ venueId, list }: { venueId: string; list: SalesPriceListDto }) {
  const { toast } = useToast()
  const save = useSaveSalesPriceList(venueId)
  const initial = React.useMemo(() => draftsOf(list), [list])
  const [tax, setTax] = React.useState(milliToPercent(list.rates.taxMilli))
  const [service, setService] = React.useState(milliToPercent(list.rates.serviceChargeMilli))
  const [serviceTaxed, setServiceTaxed] = React.useState(list.rates.serviceChargeTaxed)
  const [seasons, setSeasons] = React.useState<SeasonDraft[]>(initial.seasons)
  const [items, setItems] = React.useState<ItemDraft[]>(initial.items)
  const [error, setError] = React.useState<string | null>(null)
  const canEdit = list.canEdit

  const updateItem = (key: string, patch: Partial<ItemDraft>): void => setItems((all) => all.map((i) => (i.key === key ? { ...i, ...patch } : i)))
  const removeItem = (key: string): void => setItems((all) => all.filter((i) => i.key !== key))
  const updateSeason = (key: string, patch: Partial<SeasonDraft>): void =>
    setSeasons((all) => all.map((s) => (s.key === key ? { ...s, ...patch } : s)))
  const removeSeason = (key: string): void => {
    setSeasons((all) => all.filter((s) => s.key !== key))
    // A season's prices go with it.
    setItems((all) => all.map((i) => ({ ...i, prices: i.prices.filter((p) => p.seasonKey !== key) })))
  }

  function handleSave(): void {
    const input = toInput({ tax, service, serviceTaxed }, seasons, items)
    if (typeof input === 'string') {
      setError(input)
      return
    }
    setError(null)
    save.mutate(input, {
      onSuccess: () => toast('Price list saved. Packets already sent keep their prices.'),
      onError: (e) => setError(requestErrorMessage(e)),
    })
  }

  const ofKind = (kind: SalesItemKind) => items.filter((i) => i.kind === kind)

  return (
    <fieldset disabled={!canEdit} className="m-0 min-w-0 space-y-8 border-0 p-0">
      {canEdit ? null : <p className="m-0 text-sm text-ink-muted">You can see the price list. Changing it takes the Price list permission.</p>}

      <section className="space-y-3">
        <SectionTitle title="Tax and service charge" hint="Each item says whether it’s taxed and whether the service charge applies to it." />
        <div className="flex flex-wrap items-end gap-4">
          <Field label="Sales tax (%)" htmlFor="sales-tax">
            <Input id="sales-tax" inputMode="decimal" className="w-28 tabular-nums" value={tax} onChange={(e) => setTax(e.target.value)} />
          </Field>
          <Field label="Service charge (%)" htmlFor="service-charge">
            <Input
              id="service-charge"
              inputMode="decimal"
              className="w-28 tabular-nums"
              value={service}
              onChange={(e) => setService(e.target.value)}
            />
          </Field>
          <label className="flex items-center gap-2 pb-2.5 text-sm text-ink">
            <Checkbox checked={serviceTaxed} onCheckedChange={setServiceTaxed} />
            Tax the service charge
          </label>
        </div>
      </section>

      <section className="space-y-3">
        <SectionTitle title="Seasons" hint="Date ranges you price by. A date in no season uses each package’s “any other date” price." />
        <ul className="m-0 list-none space-y-2 p-0">
          {seasons.map((s) => (
            <li key={s.key} className="flex flex-wrap items-center gap-3">
              <Input
                size="sm"
                aria-label="Season"
                className="min-w-[10rem] flex-1"
                value={s.name}
                onChange={(e) => updateSeason(s.key, { name: e.target.value })}
                placeholder="Peak"
              />
              <DatePicker value={s.startsOn} onChange={(startsOn) => updateSeason(s.key, { startsOn })} placeholder="From" />
              <DatePicker value={s.endsOn} onChange={(endsOn) => updateSeason(s.key, { endsOn })} placeholder="To" />
              <Button variant="ghost" size="sm" aria-label={`Remove ${s.name === '' ? 'this season' : s.name}`} onClick={() => removeSeason(s.key)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
        <Button variant="outline" size="sm" onClick={() => setSeasons((all) => [...all, { key: newKey(), name: '', startsOn: '', endsOn: '' }])}>
          <Plus className="h-3.5 w-3.5" />
          Add a season
        </Button>
      </section>

      <section className="space-y-3">
        <SectionTitle title="Packages" hint="Couples choose one. Each is priced by season and day of the week." />
        <ul className="m-0 list-none space-y-3 p-0">
          {ofKind('package').map((item) => (
            <PackageCard
              key={item.key}
              item={item}
              seasons={seasons}
              tracks={list.tracks}
              onChange={(patch) => updateItem(item.key, patch)}
              onRemove={() => removeItem(item.key)}
            />
          ))}
        </ul>
        <Button variant="outline" size="sm" onClick={() => setItems((all) => [...all, blankItem('package')])}>
          <Plus className="h-3.5 w-3.5" />
          Add a package
        </Button>
      </section>

      <section className="space-y-3">
        <SectionTitle title="Per-guest prices" hint="Multiplied by the event’s guest count." />
        <ul className="m-0 list-none p-0">
          {ofKind('per_guest').map((item) => (
            <ItemRow
              key={item.key}
              item={item}
              tracks={list.tracks}
              perGuest
              onChange={(patch) => updateItem(item.key, patch)}
              onRemove={() => removeItem(item.key)}
            />
          ))}
        </ul>
        <Button variant="outline" size="sm" onClick={() => setItems((all) => [...all, blankItem('per_guest')])}>
          <Plus className="h-3.5 w-3.5" />
          Add a per-guest price
        </Button>
      </section>

      <section className="space-y-3">
        <SectionTitle title="Add-ons" hint="Extras a couple can choose, at a set price." />
        <ul className="m-0 list-none p-0">
          {ofKind('add_on').map((item) => (
            <ItemRow
              key={item.key}
              item={item}
              tracks={list.tracks}
              perGuest={false}
              onChange={(patch) => updateItem(item.key, patch)}
              onRemove={() => removeItem(item.key)}
            />
          ))}
        </ul>
        <Button variant="outline" size="sm" onClick={() => setItems((all) => [...all, blankItem('add_on')])}>
          <Plus className="h-3.5 w-3.5" />
          Add an add-on
        </Button>
      </section>

      {error === null ? null : <FormError>{error}</FormError>}
      {canEdit ? (
        <FormActions>
          <Button onClick={handleSave} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save price list'}
          </Button>
        </FormActions>
      ) : null}
    </fieldset>
  )
}
